import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
    Box,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    styled,
    Tooltip,
} from "@mui/material";
import Icon from "@/theme/vectors/bundle";
import GlobalStyle from "@/theme/styles/global";
import LogoContainer from "@/theme/partials/logo";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "@/hooks/useTranslation";

const MainContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    minHeight: "100vh",
    backgroundColor: "#f5f5f5",
}));

const TopBar = styled(Box)(({ theme }) => ({
    position: "fixed",
    top: 0,
    right: 0,
    left: 0,
    height: "60px",
    backgroundColor: "#ffffff",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 20px",
    zIndex: 1000,
    background: "url('/assets/bg-chip.png') repeat-x",
}));

const RightComponents = styled(Box)({
    display: "flex",
    alignItems: "center",
    gap: "20px",
});

const SideMenu = styled(Box)(({ isCollapsed }) => ({
    position: "fixed",
    left: 0,
    top: "60px",
    bottom: 0,
    width: isCollapsed ? "60px" : "240px",
    backgroundColor: "#ffffff",
    boxShadow: "2px 0 4px rgba(0,0,0,0.1)",
    transition: "width 0.3s ease",
    display: "flex",
    flexDirection: "column",
    overflowX: "hidden",
    overflowY: "auto",
    zIndex: 2,
    "&::-webkit-scrollbar": {
        width: "6px",
    },
    "&::-webkit-scrollbar-track": {
        background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
        background: "#888",
        borderRadius: "3px",
        transition: "background 0.2s ease",
    },
    "&::-webkit-scrollbar-thumb:hover": {
        background: "#555",
    },
    ...(isCollapsed && {
        "&::-webkit-scrollbar": {
            width: "2px",
        },
        "&::-webkit-scrollbar-thumb": {
            background: "transparent",
        },
        "&:hover::-webkit-scrollbar-thumb": {
            background: "#888",
        },
    }),
}));

const Content = styled(Box)(({ isCollapsed }) => ({
    marginLeft: isCollapsed ? "60px" : "240px",
    padding: "20px",
    transition: "all 0.3s ease",
    width: `calc(100% - ${isCollapsed ? "60px" : "240px"})`,
    height: "calc(100vh - 60px)",
    backgroundColor: "#ffffff",
    backgroundImage: "url('/assets/bg-waving.png')",
    backgroundSize: "cover",
    overflowX: "hidden",
    overflowY: "auto",
    position: "fixed",
    top: "60px",
    right: 0,
    bottom: 0,
    zIndex: 1,
    "&::-webkit-scrollbar": {
        width: "6px",
    },
    "&::-webkit-scrollbar-track": {
        background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
        background: "#888",
        borderRadius: "3px",
        transition: "background 0.2s ease",
    },
    "&::-webkit-scrollbar-thumb:hover": {
        background: "#555",
    },
}));

const MenuListItem = styled(ListItem)(({ theme, isCollapsed, isActive }) => ({
    padding: "12px 16px",
    display: "flex",
    justifyContent: isCollapsed ? "center" : "flex-start",
    alignItems: "center",
    position: "relative",
    height: "45px",
    backgroundColor: isActive ? "#f0f0f0" : "transparent",
    "&:hover": {
        cursor: "pointer",
        backgroundColor: "#f5f5f5",
        borderRadius: "8px",
        "& .MuiListItemText-primary": {
            fontWeight: "bold",
        },
        "& .chevron-icon": {
            opacity: 1,
            right: "16px",
        },
    },
    "&:focus": {
        outline: "none",
        borderRadius: "8px",
    },
}));

const MenuItemChevronIcon = styled(Box)(({ theme }) => ({
    position: "absolute",
    right: "-20px",
    opacity: 0,
    transition: "opacity 0.2s ease, right 0.2s ease",
}));

const MenuItemText = styled(ListItemText)(({ isvisible, isActive }) => ({
    display: isvisible ? "initial" : "none",
    opacity: isvisible ? 1 : 0,
    transition: "opacity 1s ease",
    transitionDelay: "0.3s",
    "& .MuiListItemText-primary": {
        fontWeight: isActive ? "bold" : "normal",
    },
}));

const LayoutCms = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [isAnimationComplete, setIsAnimationComplete] = useState(true);
    const { isAuthenticated, logout, user } = useAuth();
    const { t } = useTranslation();
    
    // Function to check if a menu item is active
    const isMenuItemActive = (path) => {
        if (!path) return false;
        return location.pathname === path || location.pathname.startsWith(`${path}/`);
    };
    
    // Get the user role from useAuth
    const userRole = user?.role || 'guest';
    
    // CMS menu items with role permissions
    const allMenuItems = [
        {
            icon: <Icon name="blockchain3" size={20} />,
            text: t('menu.product'),
            path: "/cms/product",
            allowedRoles: ['root', 'content', 'package']
        },
        {
            icon: <Icon name="shoppingCart" size={20} />,
            text: t('menu.order'),
            path: "/cms/order",
            allowedRoles: ['root', 'package']
        },
        {
            icon: <Icon name="cogwheel" size={20} />,
            text: t('menu.config'),
            path: "/cms/config",
            allowedRoles: ['root']
        }
    ];
    
    // Filter menu items based on user role
    const menuItems = allMenuItems.filter(item => 
        item.allowedRoles.includes(userRole)
    );

    const toggleMenu = () => {
        setIsCollapsed(!isCollapsed);
        setIsAnimationComplete(false);
    };

    useEffect(() => {
        const timer = setTimeout(() => setIsAnimationComplete(true), 300);
        return () => clearTimeout(timer);
    }, [isCollapsed]);

    const handleMenuItemClick = (item) => {
        if (item.action) {
            item.action();
        } else if (item.path) {
            navigate(item.path);
        }
    };
    
    // Handler for main site button click
    const handleMainSiteClick = () => {
        navigate('/knowledge');
    };
    
    // Handler for logout button click
    const handleLogoutClick = () => {
        logout();
        navigate('/login');
    };

    return (
        <MainContainer>
            {GlobalStyle}

            <TopBar>
                <LogoContainer name="Embox CMS" />
                <RightComponents>
                    <Tooltip title="Go to Main Site">
                        <IconButton 
                            onClick={handleMainSiteClick}
                            aria-label="Go to Main Site"
                        >
                            <Icon name="windowPointer" size={20} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Logout">
                        <IconButton
                            onClick={handleLogoutClick}
                            aria-label="Logout"
                        >
                            <Icon name="arrowDoorOut3" size={20} />
                        </IconButton>
                    </Tooltip>
                    {/* <IconButton>
                        <Icon name="leftChevron" size={20} />
                    </IconButton>
                    <IconButton>
                        <Icon name="rightChevron" size={20} />
                    </IconButton> */}
                </RightComponents>
            </TopBar>

            <SideMenu isCollapsed={isCollapsed}>
                <List sx={{ flex: 1, padding: "8px" }}>
                    {menuItems.map((item, index) => {
                        const isActive = isMenuItemActive(item.path);
                        return (
                            <Tooltip 
                                key={index}
                                title={isCollapsed ? item.text : ""}
                                placement="right"
                                arrow
                                disableHoverListener={!isCollapsed}
                            >
                                <MenuListItem
                                    tabIndex={0}
                                    isCollapsed={isCollapsed}
                                    isActive={isActive}
                                    onClick={() => handleMenuItemClick(item)}
                                >
                                    <ListItemIcon
                                        sx={{ 
                                            minWidth: isCollapsed ? "auto" : "40px",
                                            color: isActive ? "primary.main" : "inherit"
                                        }}
                                    >
                                        {item.icon}
                                    </ListItemIcon>

                                    {!isCollapsed && (
                                        <>
                                            <MenuItemText
                                                primary={item.text}
                                                isvisible={(
                                                    !isCollapsed && isAnimationComplete
                                                ).toString()}
                                                isActive={isActive}
                                            />
                                            <MenuItemChevronIcon className="chevron-icon">
                                                <Icon name="rightChevron" />
                                            </MenuItemChevronIcon>
                                        </>
                                    )}
                                </MenuListItem>
                            </Tooltip>
                        );
                    })}
                </List>

                <IconButton
                    onClick={toggleMenu}
                    sx={{
                        position: "fixed",
                        bottom: "20px",
                        left: isCollapsed ? "45px" : "222px",
                        transition: "left 0.3s ease",
                        backgroundColor: "#ffffff",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        "&:hover": {
                            backgroundColor: "#f5f5f5",
                        },
                    }}
                    aria-label={isCollapsed ? "Expand menu" : "Collapse menu"}
                >
                    {isCollapsed ? (
                        <Icon name="rightChevron" />
                    ) : (
                        <Icon name="leftChevron" />
                    )}
                </IconButton>
            </SideMenu>

            <Content isCollapsed={isCollapsed}>{children}</Content>
        </MainContainer>
    );
};

export default LayoutCms; 